import { Injectable } from '@angular/core';

export type Block = {
    values?: (number | null)[];
    total?: number;
    name?: string;
    axis?: string;
    stat?: string;
    type?: string;
};

export type MetricCfg = { metricCol?: string; type?: string; name?: string; axis?: string };

export type TransformOptions = {
    isTimeCategory: boolean;
    isStack: boolean;
    metricsCfg: MetricCfg[];
    seriesList: any[];
    colorPalette: string[];
    noDataColor: string;
    donutTotalFromBlock?: boolean;
    timeWindowDays?: number;


    noDataSeriesType?: 'auto' | 'donut' | 'column';
};

export type TransformInput = {
    categories?: { Display?: string }[] | { Display?: Date | string }[];
    values?: Block[];
    series?: any[];
};

export type TransformedChart = {
    categories: (string | Date)[];
    series: Array<{
        name: string;
        type: string;
        axis?: string;
        data: any[];
        stack?: true;
    }>;
};

@Injectable({ providedIn: 'root' })
export class ChartTransformService {
    transform(input: TransformInput, opts: TransformOptions): TransformedChart {
        const timeWindowDays = opts.timeWindowDays ?? 7;

        const categories = opts.isTimeCategory
            ? this.buildTimeCategories(input?.categories, timeWindowDays)
            : this.buildLabelCategories(input?.categories);

        const blocks: Block[] = Array.isArray(input?.values) ? (input!.values as Block[]) : [];
        const seriesList = Array.isArray(input?.series) ? input!.series : [];
        const multiSeries = seriesList.length > 1;

        const statKeyOf = (b: Block) => String((b?.axis ?? b?.stat ?? '')).trim();

        const getMetricIndexFromKey = (key: string): number | null => {
            const m = /^stat(\d+)$/i.exec(key);
            if (!m) return null;
            const idx = parseInt(m[1], 10) - 1;
            return Number.isFinite(idx) && idx >= 0 ? idx : null;
        };

        const hasMultipleStats = (() => {
            const keys = new Set(blocks.map(statKeyOf).filter(Boolean));
            return keys.size > 1;
        })();

        const seriesLabel = (blk: Block, idx: number, metricCfg?: MetricCfg): { name: string; type: string; axis: string } => {
            const key = statKeyOf(blk);
            const metricType = (metricCfg?.type || '').trim() || undefined;
            const metricAxis = (metricCfg?.axis || '').trim() || undefined;

            if (hasMultipleStats && metricCfg) {
                return {
                    name: (metricCfg.metricCol || '').trim() || key || blk?.name || 'Series',
                    type: (metricType || blk?.type || 'column') as string,
                    axis: metricAxis || 'left'
                };
            }

            const byNameMatch = seriesList.find(sr =>
                String(sr?.Display ?? sr?.Id ?? '').trim() === String(blk?.name ?? '').trim()
            )?.Display;
            const byIndex = seriesList[idx]?.Display;

            return {
                name: multiSeries ? (byNameMatch || byIndex || blk?.name || 'Series') : (blk?.name || 'Series'),
                type: (blk?.type || 'column') as string,
                axis: 'left'
            };
        };

        const toNumberArray = (vals?: (number | null)[]) =>
            (vals ?? []).map(v => (v == null ? null : Number(v)));

        const expectedHasDonut = blocks.some(b => (b?.type || '').toLowerCase() === 'donut')
            || opts.metricsCfg.some(m => (m?.type || '').toLowerCase() === 'donut');
        const pickNoDataType = () => {
            const pref = opts.noDataSeriesType ?? 'auto';
            if (pref === 'donut') return 'donut';
            if (pref === 'column') return 'column';
            return expectedHasDonut ? 'donut' : 'column'; // auto
        };

        const pushNoData = (arr: any[], type: 'donut' | 'column') => {
            if (type === 'donut') {
                arr.push({
                    name: 'No Data',
                    type: 'donut',
                    axis: undefined,
                    data: [{
                        category: 'No Data',
                        value: 1,
                        raw: 0,
                        total: 0,
                        color: opts.noDataColor,
                        showInLegend: false
                    }]
                });
            } else {
                arr.push({
                    name: 'No Data',
                    type: 'column',
                    axis: 'left',
                    data: categories.map(() => null as any as number),
                });
            }
        };

        const out: any[] = [];

        if (!blocks.length) {
            pushNoData(out, pickNoDataType());
        } else {
            blocks.forEach((blk, idx) => {
                const key = statKeyOf(blk);

                if (hasMultipleStats && key.toLowerCase() === 'count') return;

                const metricIdx = getMetricIndexFromKey(key);
                const metricCfg = metricIdx !== null ? opts.metricsCfg[metricIdx] : undefined;

                const { name, type: finalType, axis: finalAxis } = seriesLabel(blk, idx, metricCfg);
                const values = toNumberArray(blk.values);

                if (finalType === 'donut') {
                    const useBlockTotalFirst = opts.donutTotalFromBlock ?? true;
                    const providedTotal = Number(blk.total ?? 0);
                    const summed = (values ?? []).reduce((a, v) => a + (Number(v) || 0), 0);
                    const total = useBlockTotalFirst ? (providedTotal || summed) : summed;

                    const data =
                        !values.length || total <= 0
                            ? [{
                                category: 'No Data',
                                value: 1,
                                raw: 0,
                                total: 0,
                                color: opts.noDataColor,
                                showInLegend: false
                            }]
                            : categories.map((cat: any, i: number) => ({
                                category: cat,
                                value: Number(values[i] ?? 0),
                                color: opts.colorPalette[i % opts.colorPalette.length],
                            }));

                    out.push({ name, type: 'donut', axis: undefined, data });
                } else {
                    const data = categories.map((_, i) =>
                        values[i] == null ? null : Number(values[i])
                    ) as number[];

                    const isStackable = finalType === 'column' || finalType === 'bar' || finalType === 'area';

                    out.push({
                        name,
                        type: finalType,
                        axis: finalAxis,
                        data,
                        stack: isStackable && opts.isStack ? true : undefined
                    });
                }
            });

            if (!out.length) {
                pushNoData(out, pickNoDataType());
            }
        }

        return { categories, series: out };
    }

    private buildTimeCategories(input: TransformInput['categories'], days: number): Date[] {
        const ds = (input ?? []).map((c: any) => new Date(c?.Display));
        if (!ds.length) return ds as Date[];
        const start = ds[0];
        const end = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
        return ds.filter(d => d >= start && d <= end);
    }

    private buildLabelCategories(input: TransformInput['categories']): string[] {
        const categories = (input ?? []).map((c: any) => String(c?.Display ?? ''));
        return categories;
    }
}
